// Relative Imports
import listingExtendedActions from '../actions/listingExtendedActions';
import edapActions from '../actions/edapActions';
import {log as logToServer} from '../api/logger';
import {badgesGraphQl} from '../api/badgesGraphQl';
import {getByOwnerProperties} from '../api/byOwnerProperties';
import {rankedBadgesGraphQl} from '../api/rankedBadgesGraphQl';
import {discoveryFeedsByListingIdGraphQl} from '../api/discoveryFeedsByListingIdGraphQl';
import {unitSpacesGraphQl} from '../api/unitSpacesGraphQl';
import {contentItemsGraphQl} from '../api/contentItemsGraphQl';
import {urgencyMessageGraphQl} from '../api/urgencyMessageGraphQl';
import {houseRulesGraphQl} from '../api/houseRulesGraphQl';
import getUnitId from '../utils/getUnitId';
import {evaluateExperiment} from '../utils/abacusHelper';

async function fetchUrgencyMessage(store) {
    const {
        listingReducer: {unitApiUrl},
        systemReducer: {locale},
        tripDetailsReducer: {arriveDateText, departDateText}
    } = store.getState();
    const unitUuid = getUnitId(unitApiUrl, false);

    let data = await urgencyMessageGraphQl({
        unitUuid,
        startDate: arriveDateText,
        endDate: departDateText,
        assetVersion: 1,
        locale
    });
    store.dispatch(listingExtendedActions.fetchUrgencyMessageSuccess(data));

    if (data) {
        store.dispatch(
            edapActions.triggerEdapEvent('generic.event', {
                eventaction: 'pdp urgency scarcity test',
                eventcategory: 'pdp page'
            })
        );
    }
}

function fetchBadges(store) {
    const {
        listingReducer: {listingId},
        systemReducer: {locale}
    } = store.getState();

    return badgesGraphQl({listingId, locale}).then((data) => {
        const badgesData = data && data.badges;
        store.dispatch(listingExtendedActions.fetchBadgesSuccess(badgesData));
    });
}

async function fetchRankedBadges(store) {
    const {
        listingReducer: {listingId},
        systemReducer: {locale}
    } = store.getState();

    const data = await rankedBadgesGraphQl({listingId, locale});
    if (data.errorCode) return;
    const rankedBadges = data && data.rankedBadges;
    store.dispatch(listingExtendedActions.fetchRankedBadgesSuccess(rankedBadges));
}

function fetchBadgeData(store) {
    const evaluateRankedBadgeExp = evaluateExperiment({
        name: 'ha_pdp_ranked_badges',
        variant: 1,
        deferLogging: true
    });
    if (evaluateRankedBadgeExp) {
        fetchRankedBadges(store);
    } else {
        fetchBadges(store);
    }
}

function byOwnerPropertiesLogNoResults(message) {
    logToServer({
        fetchByOwnerProperties: true,
        fetchByOwnerPropertiesHasResults: false,
        error: message || 'ByOwner recommendation properties returned no results'
    });
}

function fetchByOwnerProperties(store) {
    const {listingReducer, systemReducer, tripDetailsReducer} = store.getState();
    return getByOwnerProperties({
        listingId: listingReducer.listingId,
        site: systemReducer.siteName,
        startDate: tripDetailsReducer.arriveDateText,
        endDate: tripDetailsReducer.departDateText
    })
        .then((response) => {
            if (!response.body) {
                store.dispatch(listingExtendedActions.fetchBy());
                byOwnerPropertiesLogNoResults();
                return;
            }

            const hasResults = !!(response.body.recommendedHits || []).length;
            if (!hasResults) {
                byOwnerPropertiesLogNoResults();
            }

            store.dispatch(listingExtendedActions.fetchByOwnerPropertiesSuccess(response.body));
        })
        .catch((error) => {
            store.dispatch(listingExtendedActions.fetchByOwnerPropertiesFailed());

            // an error fetching by owner properties, log and do nothing
            byOwnerPropertiesLogNoResults(error && error.response && error.response.body);
        });
}

function fetchDiscoveryFeed(store) {
    const {
        listingReducer: {listingId},
        systemReducer: {locale}
    } = store.getState();

    return discoveryFeedsByListingIdGraphQl({listingId, locale}).then((data) => {
        const discoveryFeedData = data && data.discoveryFeedsByListingId;

        store.dispatch(listingExtendedActions.fetchDiscoveryFeedSuccess(discoveryFeedData));
    });
}

function fetchUnitSpaces(store) {
    const {
        listingReducer: {listingId},
        systemReducer: {locale}
    } = store.getState();

    return unitSpacesGraphQl({listingId, locale}).then((data) => {
        const unitSpacesData = data && data.unitSpaces;

        store.dispatch(listingExtendedActions.fetchUnitSpacesSuccess(unitSpacesData));
    });
}

async function fetchContentItems(store) {
    const {
        listingReducer: {listingId},
        systemReducer: {locale}
    } = store.getState();

    const data = await contentItemsGraphQl({listingId, locale});

    if (data.errorCode) return;

    const unitContentItemsData = data && data.unitContentItems;
    const propertyContentItemsData = data && data.propertyContentItems;
    const unitMetadataData = data && data.unitMetadata;

    store.dispatch(
        listingExtendedActions.fetchContentItemsSuccess(
            unitContentItemsData,
            propertyContentItemsData,
            unitMetadataData
        )
    );
}

async function fetchHouseRules(store) {
    const {
        listingReducer: {legacyLodgingPolicy, unitApiUrl},
        systemReducer
    } = store.getState();
    const houseRulesResponse = await houseRulesGraphQl({
        unitUrl: unitApiUrl,
        locale: systemReducer.locale
    });
    const {errors, houseRules, message} = houseRulesResponse;
    const policy = legacyLodgingPolicy;

    if (houseRules) {
        window.analyticsdatalayer.testattributes = 'houserules:struct';
        store.dispatch(listingExtendedActions.fetchHouseRulesSuccess(houseRules));
    } else {
        // Check if errors in fetching house rules from gql partial
        if (errors) {
            logToServer({
                fetchedHouseRules: false,
                error: `getHouseRules exception.  Error: ${message}`
            });
        }

        // there are no house rules in core api for this property
        const testattributes = window.analyticsdatalayer.testattributes;

        let houseRulesAttribute =
            policy && policy.checkInTime && policy.checkOutTime
                ? 'houserules:part'
                : 'houserules:none';

        window.analyticsdatalayer.testattributes = testattributes.length
            ? `${testattributes},${houseRulesAttribute}`
            : houseRulesAttribute;
    }
}

export default fetchUrgencyMessage;

export {
    fetchBadges,
    fetchByOwnerProperties,
    fetchUnitSpaces,
    fetchContentItems,
    fetchUrgencyMessage,
    fetchHouseRules,
    fetchDiscoveryFeed,
    fetchRankedBadges,
    fetchBadgeData
};
